import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { translations } from '../translations';
import { getUserSubscription, cancelSubscription } from '../contexts/db.Subscription';
import { detectCurrency, getCurrencySymbol, getPlanPrice } from '../contexts/geolocation';
import '../styles/pages/ManageSubscription.css';

function ManageSubscription({ language }) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currency, setCurrency] = useState('EUR');
  
  const navigate = useNavigate();
  const t = translations[language];

  // Detect currency based on geolocation
  useEffect(() => {
    detectCurrency().then(setCurrency);
  }, []);

  // Get userId from localStorage
  const userId = (() => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      return userData?.id;
    } catch { return null; }
  })();

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!userId) {
        setError(t.missingUserInfo || 'User information is missing.');
        setLoading(false);
        return;
      }

      try {
        // Only show loading spinner on initial load, not on refresh
        if (refreshKey === 0) {
          setLoading(true);
        }
        const sub = await getUserSubscription(userId);
        setSubscription(sub);
      } catch (err) {
        console.error('Error fetching subscription:', err);
        setError(t.subscriptionFailed || 'Failed to load subscription details.');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [userId, refreshKey]);

  const getStatusLabel = (status) => {
    switch (status) {
      case 'trialing': return t.statusTrialing || 'Trial';
      case 'active': return t.statusActive || 'Active';
      case 'canceled': return t.statusCanceled || 'Canceled';
      case 'expired': return t.statusExpired || 'Expired';
      default: return status;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'trialing': return 'status-trial';
      case 'active': return 'status-active';
      case 'canceled': return 'status-canceled';
      case 'expired': return 'status-expired';
      default: return '';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleCancelClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmCancel = async () => {
    if (!subscription?.id) return;

    try {
      setCanceling(true);
      setError(null);
      
      await cancelSubscription(subscription.id);
      
      // Close modal and reload page to refresh data
      setShowConfirmModal(false);
      
      // Create a completely new object to force React re-render
      const updatedSubscription = {
        ...subscription,
        plan: subscription.plan ? { ...subscription.plan } : null,
        status: 'canceled',
        canceled_at: new Date().toISOString()
      };
      
      console.log('Setting new subscription with status:', updatedSubscription.status);
      
      setSubscription(updatedSubscription);
      setSuccessMessage(t.subscriptionCanceledSuccess || 'Your subscription has been canceled.');
      setRefreshKey(prev => prev + 1); // Force re-render
      setCanceling(false);
      window.location.reload();
      
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setError(t.cancelError || 'Failed to cancel subscription. Please try again.');
      setCanceling(false);
    }
  };

  const canCancel = subscription && 
    (subscription.status === 'trialing' || subscription.status === 'active');

  if (loading) {
    return (
      <div className="manage-subscription-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
          <p>{t.loadingSubscription || 'Loading subscription details...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-subscription-container">
      <div className="subscription-card">
        <h1>{t.mySubscription || 'My Subscription'}</h1>
        
        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}

        {!subscription ? (
          <div className="no-subscription">
            <div className="no-subscription-icon">📭</div>
            <p>{t.noActiveSubscription || 'No active subscription'}</p>
            <button 
              className="subscribe-btn"
              onClick={() => navigate('/subscription')}
            >
              {t.subscribeNow || 'Subscribe Now'}
            </button>
          </div>
        ) : (
          <div key={`sub-${subscription.status}-${refreshKey}`}>
            <div className="subscription-details">
              <div className="detail-row">
                <span className="detail-label">{t.planName || 'Plan'}:</span>
                <span className="detail-value">{subscription.plan?.name || '-'}</span>
              </div>
              
              <div className="detail-row">
                <span className="detail-label">{t.price || 'Price'}:</span>
                <span className="detail-value">
                  {getCurrencySymbol(getPlanPrice(subscription.plan, currency).currency)}{getPlanPrice(subscription.plan, currency).price || '-'}
                </span>
              </div>
              
              <div className="detail-row">
                <span className="detail-label">{t.subscriptionStatus || 'Status'}:</span>
                <span className={`detail-value status-badge ${getStatusClass(subscription.status)}`}>
                  {getStatusLabel(subscription.status)}
                </span>
              </div>
              
              {subscription.status === 'trialing' && subscription.current_period_end && (
                <div className="detail-row">
                  <span className="detail-label">{t.trialEndsOn || 'Trial ends on'}:</span>
                  <span className="detail-value">{formatDate(subscription.current_period_end)}</span>
                </div>
              )}
              
              {subscription.status === 'active' && subscription.current_period_end && (
                <div className="detail-row">
                  <span className="detail-label">{t.nextBillingDate || 'Next billing date'}:</span>
                  <span className="detail-value">{formatDate(subscription.current_period_end)}</span>
                </div>
              )}
            </div>

            {subscription.status === 'trialing' && (
              <div className="trial-limitation-info">
                <p>{t.trialLimitationInfo || 'During your trial period, your child can only play Door 1 in the game. Once your subscription becomes active, all levels will be unlocked.'}</p>
              </div>
            )}

            {canCancel && (
              <div className="subscription-actions">
                <button 
                  className="cancel-btn"
                  onClick={handleCancelClick}
                  disabled={canceling}
                >
                  {t.cancelSubscription || 'Cancel Subscription'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t.cancelConfirmTitle || 'Cancel Subscription?'}</h2>
            <p>{t.cancelConfirmMessage || 'Are you sure you want to cancel your subscription? You will lose access at the end of your current period.'}</p>
            <div className="modal-actions">
              <button 
                className="modal-btn secondary"
                onClick={() => setShowConfirmModal(false)}
                disabled={canceling}
              >
                {t.keepSubscription || 'Keep Subscription'}
              </button>
              <button 
                className="modal-btn danger"
                onClick={handleConfirmCancel}
                disabled={canceling}
              >
                {canceling ? (t.processing || 'Processing...') : (t.confirmCancel || 'Yes, Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageSubscription;
